import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext.js';
import { useTerms } from '../hooks/useTerms.js';
import { supabase } from '../services/api/client.js';
import { CircularProgress, Box, Typography } from '@mui/material';
import { AppContext } from './AppContext.js';

export const AppProvider = ({ children }) => {
    // --- 1. Hooks 호출부 ---
    const [darkMode, setDarkMode] = useState(() => {
        if (typeof window !== 'undefined') {
            const stored = localStorage.getItem('theme');
            if (stored) {
                return stored === 'dark';
            }
            return window.matchMedia('(prefers-color-scheme: dark)').matches;
        }
        return false;
    });

    useEffect(() => {
        if (typeof window === 'undefined') return;
        localStorage.setItem('theme', darkMode ? 'dark' : 'light');
    }, [darkMode]);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleChange = (e) => {
            const stored = localStorage.getItem('theme');
            if (!stored) {
                setDarkMode(e.matches);
            }
        };

        if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener('change', handleChange);
        } else {
            mediaQuery.addListener(handleChange);
        }

        return () => {
            if (mediaQuery.removeEventListener) {
                mediaQuery.removeEventListener('change', handleChange);
            } else {
                mediaQuery.removeListener(handleChange);
            }
        };
    }, []);
    const [openSettings, setOpenSettings] = useState(false);
    const [openWrongAnswers, setOpenWrongAnswers] = useState(false);
    const [openSubjectTerms, setOpenSubjectTerms] = useState(false);
    const [openAddDialog, setOpenAddDialog] = useState(false);
    const [openAddMyTermDialog, setOpenAddMyTermDialog] = useState(false);
    const [openAddMyDefinitionDialog, setOpenAddMyDefinitionDialog] = useState(false);
    const [selectedSubject, setSelectedSubject] = useState(null);
    const [selectedTerm, setSelectedTerm] = useState(null);
    const [wrongAnswers, setWrongAnswers] = useState([]);
    const [isLoadingWrongAnswers, setIsLoadingWrongAnswers] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    const [openMyTermsList, setOpenMyTermsList] = useState(false);

    const { user, loading: authLoading } = useAuth();
    const { terms, loading: termsLoading, loadTerms } = useTerms();

    // --- 2. 메모 및 콜백 Hooks ---
    const subjects = useMemo(() => {
        if (!terms) return [];
        return [...new Set(terms.map(term => term.subject))].sort();
    }, [terms]);

    const loadWrongAnswers = useCallback(async () => {
        if (!user) return;
        setIsLoadingWrongAnswers(true);
        try {
            const { data, error } = await supabase.from('wrong_answers').select('*').eq('user_id', user.id).order('created_at', { ascending: false });
            if (error) throw error;
            setWrongAnswers(data || []);
        } catch (error) {
            console.error('오답 노트 로드 중 오류:', error);
            setSnackbar({ open: true, message: '오답 노트를 불러오는 중 오류가 발생했습니다.', severity: 'error' });
        } finally {
            setIsLoadingWrongAnswers(false);
        }
    }, [user]);

    const addToWrongAnswers = useCallback(async (wrongAnswer) => {
        if (!user) return false;
        try {
            let keywords = [];
            if (wrongAnswer.term_id || wrongAnswer.termId) {
                const { data: termData } = await supabase.from('terms').select('keywords').eq('id', wrongAnswer.term_id || wrongAnswer.termId).single();
                if (termData && termData.keywords) keywords = termData.keywords;
            }
            const newWrongAnswer = {
                user_id: user.id,
                term_id: wrongAnswer.term_id || wrongAnswer.termId,
                term: wrongAnswer.term,
                subject: wrongAnswer.subject,
                definition: wrongAnswer.definition,
                wrong_answer: wrongAnswer.wrongAnswer || wrongAnswer.wrong_answer || '',
                keywords: keywords,
                hint_level: wrongAnswer.hint_level || 0,
                keywords_matched: wrongAnswer.keywords_matched || 0,
                total_keywords: wrongAnswer.total_keywords || 0,
                score: wrongAnswer.score || 0,
                created_at: new Date().toISOString()
            };
            const { error } = await supabase.from('wrong_answers').insert(newWrongAnswer);
            if (error) throw error;
            loadWrongAnswers();
            return true;
        } catch (error) {
            console.error('오답 저장 중 오류:', error);
            setSnackbar({ open: true, message: '오답 저장 중 오류가 발생했습니다: ' + error.message, severity: 'error' });
            return false;
        }
    }, [user, loadWrongAnswers]);

    const handleRemoveWrongAnswer = useCallback(async (wrongAnswerId) => {
        if (!user) return;
        try {
            const { error } = await supabase.from('wrong_answers').delete().eq('id', wrongAnswerId).eq('user_id', user.id);
            if (error) throw error;
            setWrongAnswers(prev => prev.filter(wa => wa.id !== wrongAnswerId));
            setSnackbar({ open: true, message: '오답이 삭제되었습니다.', severity: 'success' });
        } catch (error) {
            console.error('오답 삭제 중 오류:', error);
            setSnackbar({ open: true, message: '오답 삭제 중 오류가 발생했습니다.', severity: 'error' });
        }
    }, [user]);

    useEffect(() => {
        if (user) {
            loadTerms();
            loadWrongAnswers();
        } else {
            setWrongAnswers([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const handleSubjectClick = useCallback((subject) => {
        setSelectedSubject(subject);
        setOpenSubjectTerms(true);
    }, []);

    const getSubjectCount = useCallback((subject) => {
        if (!terms) return 0;
        return terms.filter(term => term.subject === subject).length;
    }, [terms]);

    // --- 3. 로딩 가드 ---
    if (authLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>인증 정보를 확인 중입니다...</Typography>
            </Box>
        );
    }

    // --- 4. Context 값 정의 및 Provider 렌더링 ---
    const value = {
        darkMode, setDarkMode,
        openSettings, setOpenSettings,
        openWrongAnswers, setOpenWrongAnswers,
        openSubjectTerms, setOpenSubjectTerms,
        openAddDialog, setOpenAddDialog,
        openAddMyTermDialog, setOpenAddMyTermDialog,
        openAddMyDefinitionDialog, setOpenAddMyDefinitionDialog,
        selectedSubject, setSelectedSubject,
        selectedTerm, setSelectedTerm,
        terms,
        loading: termsLoading,
        wrongAnswers,
        isLoadingWrongAnswers,
        loadWrongAnswers,
        addToWrongAnswers,
        handleRemoveWrongAnswer,
        handleSubjectClick,
        getSubjectCount,
        snackbar, setSnackbar,
        openMyTermsList, setOpenMyTermsList,
        subjects,
        user,
        isLoadingTerms: termsLoading,
    };

    return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export default AppProvider;
